// Photo vault v2 (card #3995): the write-once byte store for field photos.
//
// Evidence (card note, real iPhone, 8 Oct 2026): a Blob written by a process
// that has since died is still readable, but REWRITING the row that holds it
// makes it unreadable. The old queue kept the Blob in the same row as the
// upload status, and every status change rewrote the row. So the bytes now live
// here, in a row of their own, as a plain ArrayBuffer:
//
//   - written once, at capture (writeToVault)
//   - read straight back and compared (length and SHA-256) before the photo is
//     called "verified"
//   - never updated: nothing in the upload or status code touches this table
//   - read for upload (readFromVault), re-checked against the stored length and
//     hash, so bytes that came back wrong are refused rather than uploaded
//
// pending_images holds the metadata (status, retry state) and refers to these
// bytes by blobKey. Rows from before this change keep their embedded blob and
// are read through the legacy path in field_image_queue.js.
//
// Nothing here throws. A failed write or read resolves { ok: false, reason }.

import db from "./field_db"
import { logEvent, logError, getPlatformSnapshot } from "./field_logger"

// Reasons a write or a read-back can fail.
export const VAULT_REASON = {
  EMPTY:           "empty",
  SOURCE_UNREADABLE: "source_unreadable",
  WRITE_FAILED:    "write_failed",
  READ_FAILED:     "read_failed",
  MISSING:         "missing",
  BAD_BYTES:       "bad_bytes",
  LENGTH_MISMATCH: "length_mismatch",
  HASH_MISMATCH:   "hash_mismatch"
}

// A row whose bytes live in pending_blobs rather than in the row itself.
export function isVaultRow(record) {
  return record?.blobStore === "vault"
}

// Lowercase hex SHA-256 of an ArrayBuffer, or null where crypto.subtle is not
// available (an insecure context). Callers fall back to a byte comparison.
export async function sha256Hex(buffer) {
  try {
    const digest = await crypto.subtle.digest("SHA-256", buffer)
    return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("")
  } catch {
    return null
  }
}

function _sameBytes(a, b) {
  if (a.byteLength !== b.byteLength) return false
  const left = new Uint8Array(a)
  const right = new Uint8Array(b)
  for (let i = 0; i < left.length; i++) if (left[i] !== right[i]) return false
  return true
}

function _failure(reason, error) {
  return { ok: false, reason, errorName: error?.name || null, errorMessage: error?.message || null }
}

// Writes the photo's bytes once, reads them back and compares. Logs
// "Photo vault verified" or "Photo vault failed" (with the platform).
//
//   { ok: true,  sha256, byteLength, type }
//   { ok: false, reason, errorName, errorMessage }
//
// A failed write removes any partial row so a half-written photo is never read
// later. The caller keeps its in-memory copy.
export async function writeToVault(blobKey, file) {
  const platform = getPlatformSnapshot().os
  const fail = async (result, extra = {}) => {
    try { await db.pending_blobs.delete(blobKey) } catch { /* nothing to clean up */ }
    logError("upload", "Photo vault failed", {
      platform,
      blobKey,
      reason:       result.reason,
      name:         result.errorName,
      message:      result.errorMessage,
      sizeBytes:    file?.size ?? null,
      ...extra
    }).catch(() => {})
    // Card #4215 -- lets field_storage_health_controller take and log a
    // storage reading at the moment of the failure, to correlate it with the
    // phone's storage state. Never blocks or changes the vault's own outcome.
    try { window.dispatchEvent(new CustomEvent("field:vault-failed", { detail: { reason: result.reason, source: "vault" } })) } catch { /* non-browser context */ }
    return result
  }

  let buffer
  try {
    buffer = await file.arrayBuffer()
  } catch (error) {
    return fail(_failure(VAULT_REASON.SOURCE_UNREADABLE, error))
  }
  if (buffer.byteLength === 0) return fail(_failure(VAULT_REASON.EMPTY))

  const sha256 = await sha256Hex(buffer)
  const byteLength = buffer.byteLength
  const type = file.type || "application/octet-stream"

  try {
    await db.pending_blobs.put({ blobKey, bytes: buffer, type, sha256, byteLength, createdAt: new Date().toISOString() })
  } catch (error) {
    return fail(_failure(VAULT_REASON.WRITE_FAILED, error), { stage: "write" })
  }

  let stored
  try {
    stored = await db.pending_blobs.get(blobKey)
  } catch (error) {
    return fail(_failure(VAULT_REASON.READ_FAILED, error), { stage: "read_back" })
  }

  const check = await _checkRow(stored, { expectedLength: byteLength, expectedHash: sha256, original: buffer })
  if (!check.ok) return fail(check, { stage: "read_back" })

  logEvent("upload", "Photo vault verified", { platform, blobKey, sizeBytes: byteLength }).catch(() => {})
  return { ok: true, sha256, byteLength, type }
}

// Compares a stored row to what it should hold. `original` (the bytes just
// written) allows a byte-for-byte check where no hash is available.
async function _checkRow(row, { expectedLength, expectedHash, original = null }) {
  if (!row) return _failure(VAULT_REASON.MISSING)
  if (!(row.bytes instanceof ArrayBuffer)) return _failure(VAULT_REASON.BAD_BYTES)
  if (row.bytes.byteLength !== expectedLength) return _failure(VAULT_REASON.LENGTH_MISMATCH)

  if (expectedHash) {
    const actual = await sha256Hex(row.bytes)
    if (actual && actual !== expectedHash) return _failure(VAULT_REASON.HASH_MISMATCH)
    if (actual) return { ok: true }
  }
  if (original && !_sameBytes(original, row.bytes)) return _failure(VAULT_REASON.HASH_MISMATCH)
  return { ok: true }
}

// Reads a photo's bytes back for upload as an in-memory File, after checking
// them against the length and hash stored with them. Pass verify: false for a
// preview thumbnail, where a hash per render is wasted work.
//
//   { ok: true,  file, sha256, byteLength }
//   { ok: false, reason, errorName, errorMessage }
export async function readFromVault(blobKey, { name = "image.webp", verify = true } = {}) {
  let row
  try {
    row = await db.pending_blobs.get(blobKey)
  } catch (error) {
    return _failure(VAULT_REASON.READ_FAILED, error)
  }

  if (verify) {
    const check = await _checkRow(row, { expectedLength: row?.byteLength, expectedHash: row?.sha256 })
    if (!check.ok) return check
  } else if (!row || !(row.bytes instanceof ArrayBuffer)) {
    return _failure(row ? VAULT_REASON.BAD_BYTES : VAULT_REASON.MISSING)
  }

  const file = new File([row.bytes], name, { type: row.type || "image/webp" })
  return { ok: true, file, sha256: row.sha256 || null, byteLength: row.byteLength }
}

export async function deleteFromVault(blobKey) {
  try {
    await db.pending_blobs.delete(blobKey)
  } catch { /* an orphan is swept by purgeOrphanedVaultBlobs */ }
}
